import { Head, router, usePage } from "@inertiajs/react";
import { HistoryIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalModeConfig } from "@/components/admin-portal/routing-mode/global-settings";
import { HistoryPanel } from "@/components/admin-portal/routing-mode/history-panel";
import { LoadingVerb } from "@/components/admin-portal/routing-mode/loading-verb";
import { LocationsTable } from "@/components/admin-portal/routing-mode/locations-table";
import { Button } from "@/components/ui/button";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import type {
	ExternalProp,
	RoutingHistoryFilters,
	RoutingHistoryOptions,
	RoutingLocationPolicy,
	RoutingPolicyChange,
	RoutingSettings,
} from "@/types/admin-portal/routing-mode";
import type { GlobalPageProps } from "@/types/globals";

interface PageProps {
	settings?: ExternalProp<RoutingSettings>;
	locations?: ExternalProp<RoutingLocationPolicy[]>;
	changes?: ExternalProp<RoutingPolicyChange[]>;
	historyFilters: RoutingHistoryFilters;
	historyOptions?: RoutingHistoryOptions;
}

const HISTORY_PROPS = ["changes", "historyFilters", "historyOptions"];

const loading = <LoadingVerb />;

export default function Index({
	settings,
	locations,
	changes,
	historyFilters,
	historyOptions,
}: PageProps) {
	const { t } = useTranslation("routing-mode");
	// The page is only reachable when routing_mode_menu is present.
	const routes =
		usePage<GlobalPageProps>().props.adminPortal.router.adminPortal.routingMode;
	// ?history=1 keeps the sheet open across a refresh or a shared link.
	const [historyOpen, setHistoryOpen] = useState(
		() => new URLSearchParams(window.location.search).get("history") === "1",
	);
	// The history table seeds its filter fields once, on mount, so it waits for the
	// rows matching the filters it was opened with.
	const [historyReady, setHistoryReady] = useState(true);

	// URL changes replace the entry: the sheet's open state lives here, so a Back
	// press that restored ?history=1 would not reopen it.
	function openHistory(filters: RoutingHistoryFilters = {}) {
		setHistoryOpen(true);
		setHistoryReady(false);
		router.get(
			window.location.pathname,
			{ history: 1, location_id: filters.locationId },
			{
				only: HISTORY_PROPS,
				preserveState: true,
				preserveScroll: true,
				replace: true,
				onFinish: () => setHistoryReady(true),
			},
		);
	}

	function closeHistory() {
		setHistoryOpen(false);
		router.get(
			window.location.pathname,
			{},
			{
				only: ["historyFilters"],
				preserveState: true,
				preserveScroll: true,
				replace: true,
			},
		);
	}

	return (
		<>
			<Head title={t("title")} />

			<div className="flex flex-col gap-4 p-4">
				<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<div>
						<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
							<h1 className="text-2xl font-bold tracking-tight">
								{t("title")}
							</h1>
							{/* Both the global mode shown and the location list come from this backend. */}
							{settings?.data?.currentEnv && (
								<span className="inline-flex items-center gap-1.5 rounded-full border bg-muted/50 px-2.5 py-0.5 text-xs">
									<span
										aria-hidden
										className="size-1.5 rounded-full bg-primary"
									/>
									<span className="text-muted-foreground">{t("env_chip")}</span>
									<span className="font-semibold">
										{settings.data.currentEnv}
									</span>
								</span>
							)}
						</div>
						<p className="text-sm text-muted-foreground">{t("description")}</p>
					</div>
					<Button
						variant="outline"
						className="shrink-0 self-start"
						onClick={() => openHistory()}
					>
						<HistoryIcon />
						{t("history.open")}
					</Button>
				</div>

				{/* The loader shows only on the first load: a reload after a save keeps the
				    old rows on screen until the new ones land, instead of unmounting the
				    table (and its filters) behind a spinner. Inertia fetches deferred
				    props on its own, so no <Deferred> wrapper is needed. */}
				{settings && locations ? (
					<>
						<GlobalModeConfig
							settings={settings}
							updateUrl={routes?.settings ?? ""}
						/>
						<LocationsTable
							locations={locations}
							currentEnv={settings.data?.currentEnv}
							locationUrl={routes?.location ?? ""}
							bulkUrl={routes?.bulk ?? ""}
							onShowHistory={(locationId) => openHistory({ locationId })}
						/>
					</>
				) : (
					loading
				)}
			</div>

			<Sheet
				open={historyOpen}
				onOpenChange={(open) => !open && closeHistory()}
			>
				<SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-3xl">
					<SheetHeader className="pr-6">
						<SheetTitle>{t("history.title")}</SheetTitle>
						<SheetDescription>{t("history.description")}</SheetDescription>
					</SheetHeader>
					{historyReady && changes && historyOptions ? (
						<HistoryPanel
							changes={changes}
							filters={historyFilters}
							options={historyOptions}
						/>
					) : (
						loading
					)}
				</SheetContent>
			</Sheet>
		</>
	);
}
